import { useMemo, useState } from 'react';
import {
  useFetchReportsSummaryQuery,
  useFetchRevenueReportQuery,
  useFetchVariableExpensesReportQuery,
  useFetchFixedExpensesReportQuery,
  useFetchRecurringExpensesReportQuery,
  useFetchOtherRevenueReportQuery,
  useFetchTransferFeesReportQuery,
} from '../../api';
import { formatAmount } from '../../utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const RevenueReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [currency, setCurrency] = useState<string>('RWF');

  const range = useMemo(() => {
    const start = startDate ?? dayjs().startOf('month');
    const end = endDate ?? dayjs();
    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      from: start.startOf('day').toISOString(),
      to: end.endOf('day').toISOString(),
    };
  }, [startDate, endDate]);

  const { data: menuSummary, isFetching: menuFetching } = useFetchReportsSummaryQuery({
    from: range.from,
    to: range.to,
  });
  const { data: officesRevenue, isFetching: officesFetching } = useFetchRevenueReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: expensesReport, isFetching: expensesFetching } = useFetchVariableExpensesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: fixedReport, isFetching: fixedFetching } = useFetchFixedExpensesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: recurringReport, isFetching: recurringFetching } = useFetchRecurringExpensesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: otherRevenueReport, isFetching: otherRevenueFetching } = useFetchOtherRevenueReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { data: transferFeesReport, isFetching: transferFeesFetching } = useFetchTransferFeesReportQuery({
    startDate: range.startDate,
    endDate: range.endDate,
  });

  const isLoading = menuFetching || officesFetching || expensesFetching || fixedFetching || recurringFetching || otherRevenueFetching || transferFeesFetching;

  // Aggregate Other Revenue per currency from its items (backend returns items, not byCurrency).
  const otherRevenueByCurrency: Record<string, number> = {};
  const otherRevenueItems: any[] = otherRevenueReport?.items || [];
  for (const item of otherRevenueItems) {
    const c = (item?.currency && String(item.currency).trim()) || 'RWF';
    otherRevenueByCurrency[c] = (otherRevenueByCurrency[c] || 0) + Number(item?.amount || 0);
  }

  // Discover every currency present in the data, plus RWF (always selectable).
  const allCurrencies = Array.from(new Set<string>([
    'RWF',
    ...Object.keys((expensesReport?.byCurrency || {}) as Record<string, number>),
    ...Object.keys((fixedReport?.byCurrency || {}) as Record<string, number>),
    ...Object.keys((expensesReport?.feesByCurrency || {}) as Record<string, number>),
    ...Object.keys((fixedReport?.feesByCurrency || {}) as Record<string, number>),
    ...Object.keys((recurringReport?.byCurrency || {}) as Record<string, number>),
    ...Object.keys((recurringReport?.feesByCurrency || {}) as Record<string, number>),
    ...Object.keys(otherRevenueByCurrency),
  ]));

  // Revenue sources without explicit currency tracking are treated as RWF.
  const RWF_ONLY = currency === 'RWF';
  const menuRevenue = RWF_ONLY ? Number(menuSummary?.totalRevenue || 0) : 0;
  const membershipRevenue = RWF_ONLY ? Number(officesRevenue?.membershipRevenue || 0) : 0;
  const rentalRevenue = RWF_ONLY ? Number(officesRevenue?.rentalRevenue || 0) : 0;
  const officeRevenue = RWF_ONLY ? Number(officesRevenue?.officeRevenue || 0) : 0;
  const officesTotal = membershipRevenue + rentalRevenue + officeRevenue;
  const otherRevenue = Number(otherRevenueByCurrency[currency] || 0);

  // Operating expenses: pull only the slice for the selected currency.
  const operatingExpenses = Number((expensesReport?.byCurrency as Record<string, number> | undefined)?.[currency] || 0);
  // Fixed expenses = legacy Fixed Expenses rows + occurrences recorded under Recurring Expenses.
  const fixedExpenses = Number((fixedReport?.byCurrency as Record<string, number> | undefined)?.[currency] || 0)
    + Number((recurringReport?.byCurrency as Record<string, number> | undefined)?.[currency] || 0);

  // Account-level fees — variable & fixed have per-currency maps; payment & transfer fees
  // aren't currency-tagged on the backend yet, so they're applied only when viewing RWF.
  const paymentFees = RWF_ONLY ? Number(officesRevenue?.totalFees || 0) : 0;
  const variableExpenseFees = Number((expensesReport?.feesByCurrency as Record<string, number> | undefined)?.[currency] || 0);
  const fixedExpenseFees = Number((fixedReport?.feesByCurrency as Record<string, number> | undefined)?.[currency] || 0)
    + Number((recurringReport?.feesByCurrency as Record<string, number> | undefined)?.[currency] || 0);
  const transferFees = RWF_ONLY ? Number(transferFeesReport?.totalFees || 0) : 0;
  const totalAccountFees = paymentFees + variableExpenseFees + fixedExpenseFees + transferFees;

  const totalRevenue = menuRevenue + officesTotal + otherRevenue;
  const operatingExpensesTotal = operatingExpenses + fixedExpenses;
  const net = totalRevenue - operatingExpensesTotal - totalAccountFees;
  const isProfit = net >= 0;

  const revenueBreakdown = [
    { label: 'Menu', value: menuRevenue, color: 'bg-emerald-500' },
    { label: 'Membership', value: membershipRevenue, color: 'bg-blue-500' },
    { label: 'Rental', value: rentalRevenue, color: 'bg-purple-500' },
    { label: 'Office', value: officeRevenue, color: 'bg-amber-500' },
    { label: 'Other Revenue', value: otherRevenue, color: 'bg-teal-500' },
  ];

  const mergedExpenseCategories: Record<string, number> = {};
  if (expensesReport?.byCategory) {
    for (const [k, v] of Object.entries(expensesReport.byCategory as Record<string, number>)) {
      mergedExpenseCategories[k] = (mergedExpenseCategories[k] || 0) + Number(v);
    }
  }
  if (fixedReport?.byCategory) {
    for (const [k, v] of Object.entries(fixedReport.byCategory as Record<string, number>)) {
      mergedExpenseCategories[k] = (mergedExpenseCategories[k] || 0) + Number(v);
    }
  }
  const expenseByCategory: [string, number][] = Object.entries(mergedExpenseCategories).sort((a, b) => b[1] - a[1]);

  // Per-currency operating expense totals (so RWF and USD aren't lumped together).
  const operatingByCurrency: Record<string, number> = {};
  const feesByCurrencyMerged: Record<string, number> = {};
  const addCurrencyMap = (target: Record<string, number>, src: any) => {
    if (!src) return;
    for (const [cur, value] of Object.entries(src as Record<string, number>)) {
      const key = cur || 'RWF';
      target[key] = (target[key] || 0) + Number(value || 0);
    }
  };
  addCurrencyMap(operatingByCurrency, expensesReport?.byCurrency);
  addCurrencyMap(operatingByCurrency, fixedReport?.byCurrency);
  addCurrencyMap(feesByCurrencyMerged, expensesReport?.feesByCurrency);
  addCurrencyMap(feesByCurrencyMerged, fixedReport?.feesByCurrency);
  const operatingByCurrencyEntries = Object.entries(operatingByCurrency).sort((a, b) => b[1] - a[1]);
  const feesByCurrencyEntries = Object.entries(feesByCurrencyMerged).sort((a, b) => b[1] - a[1]);

  const maxComparison = Math.max(totalRevenue, operatingExpensesTotal, totalAccountFees) || 1;

  const periodLabel = `${dayjs(range.startDate).format('MMM D, YYYY')} – ${dayjs(range.endDate).format('MMM D, YYYY')}`;

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Typography variant="h6" className="font-bold text-slate-800">Revenue vs Expenses</Typography>
          <p className="text-xs text-slate-500 mt-0.5">All numbers below are scoped to <span className="font-semibold text-slate-700">{periodLabel}</span> in <span className="font-semibold text-slate-700">{currency}</span></p>
        </div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3">
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small' } }} />
            <FormControl size="small" style={{ minWidth: 110 }}>
              <Select value={currency} onChange={(e) => setCurrency(String(e.target.value))}>
                {allCurrencies.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </LocalizationProvider>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-500 italic">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <NetTile
              label={isProfit ? 'Net Profit' : 'Net Loss'}
              value={`${currency} ${formatAmount(Math.abs(net))}`}
              positive={isProfit}
            />
            <SummaryTile label="Total Revenue" value={`${currency} ${formatAmount(totalRevenue)}`} accent="emerald" />
            <SummaryTile label="Operating Expenses" value={`${currency} ${formatAmount(operatingExpensesTotal)}`} accent="rose" />
            <SummaryTile label="Account Fees" value={`${currency} ${formatAmount(totalAccountFees)}`} accent="rose" />
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 text-sm mb-4">Revenue vs Expenses</h3>
            <div className="space-y-4">
              <ComparisonBar label="Revenue" value={totalRevenue} max={maxComparison} color="bg-emerald-500" />
              <ComparisonBar label="Operating Expenses" value={operatingExpensesTotal} max={maxComparison} color="bg-rose-500" />
              <ComparisonBar label="Account Fees" value={totalAccountFees} max={maxComparison} color="bg-rose-300" />
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-600 space-y-1">
              {totalRevenue > 0 ? (
                <>
                  <div>
                    Operating expense ratio:{' '}
                    <span className="font-bold text-slate-900">
                      {((operatingExpensesTotal / totalRevenue) * 100).toFixed(1)}%
                    </span>{' '}
                    of revenue
                  </div>
                  <div>
                    Account fees ratio:{' '}
                    <span className="font-bold text-slate-900">
                      {((totalAccountFees / totalRevenue) * 100).toFixed(1)}%
                    </span>{' '}
                    of revenue
                  </div>
                </>
              ) : (
                <span className="text-slate-400">No revenue in this period</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Revenue Sources</h3>
              {totalRevenue === 0 ? (
                <p className="text-slate-400 text-sm">No revenue recorded</p>
              ) : (
                <div className="space-y-3">
                  {revenueBreakdown.map((row) => {
                    const pct = totalRevenue > 0 ? (row.value / totalRevenue) * 100 : 0;
                    return (
                      <div key={row.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{row.label}</span>
                          <span className="text-sm font-bold text-slate-900">
                            {currency} {formatAmount(row.value)}{' '}
                            <span className="text-xs text-slate-400 font-medium">({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${row.color} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Expense Categories</h3>
              {expenseByCategory.length === 0 ? (
                <p className="text-slate-400 text-sm">No expenses recorded</p>
              ) : (
                <div className="space-y-3">
                  {expenseByCategory.map(([category, value]) => {
                    const pct = operatingExpensesTotal > 0 ? (value / operatingExpensesTotal) * 100 : 0;
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{category}</span>
                          <span className="text-sm font-bold text-slate-900">
                            {currency} {formatAmount(value)}{' '}
                            <span className="text-xs text-slate-400 font-medium">({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 text-sm mb-4">Operating Expenses by Currency</h3>
            {operatingByCurrencyEntries.length === 0 ? (
              <p className="text-slate-400 text-sm">No operating expenses in this period</p>
            ) : (
              <div className="space-y-3">
                {operatingByCurrencyEntries.map(([currency, value]) => {
                  const feeForCurrency = feesByCurrencyMerged[currency] || 0;
                  return (
                    <div key={currency} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                      <span className="text-sm font-medium text-slate-700">{currency}</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-slate-900">{currency} {formatAmount(value)}</div>
                        {feeForCurrency > 0 && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            + Fees: {currency} {formatAmount(feeForCurrency)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">Currencies are listed separately — totals are not summed across different currencies.</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
              <h3 className="font-bold text-slate-800 text-sm">Period Summary</h3>
            </div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                <SummaryRow label="Menu revenue" value={menuRevenue} currency={currency} />
                <SummaryRow label="Membership revenue" value={membershipRevenue} currency={currency} />
                <SummaryRow label="Rental revenue" value={rentalRevenue} currency={currency} />
                <SummaryRow label="Office revenue" value={officeRevenue} currency={currency} />
                <SummaryRow label="Other revenue" value={otherRevenue} currency={currency} />
                <tr className="bg-emerald-50 border-t border-slate-200">
                  <td className="py-3 px-5 font-bold text-slate-900">Total revenue</td>
                  <td className="py-3 px-5 text-right font-bold text-emerald-700">{currency} {formatAmount(totalRevenue)}</td>
                </tr>
                <tr className="bg-slate-50 border-t-4 border-slate-200">
                  <td className="py-2 px-5 text-xs font-bold text-slate-500 uppercase tracking-widest">Operating Expenses</td>
                  <td className="py-2 px-5"></td>
                </tr>
                <SummaryRow label="Variable expenses" value={operatingExpenses} currency={currency} />
                <SummaryRow label="Fixed expenses" value={fixedExpenses} currency={currency} />
                <tr className="bg-rose-50 border-t border-slate-200">
                  <td className="py-3 px-5 font-bold text-slate-900">Total operating expenses</td>
                  <td className="py-3 px-5 text-right font-bold text-rose-700">- {currency} {formatAmount(operatingExpensesTotal)}</td>
                </tr>

                <tr className="bg-slate-50 border-t-4 border-slate-200">
                  <td className="py-2 px-5 text-xs font-bold text-slate-500 uppercase tracking-widest">Account Fees (paid to institutions)</td>
                  <td className="py-2 px-5"></td>
                </tr>
                <SummaryRow label="Fees on incoming payments" value={paymentFees} currency={currency} />
                <SummaryRow label="Fees on variable expense payouts" value={variableExpenseFees} currency={currency} />
                <SummaryRow label="Fees on fixed expense payouts" value={fixedExpenseFees} currency={currency} />
                <SummaryRow label="Fees on account transfers" value={transferFees} currency={currency} />
                <tr className="bg-rose-50 border-t border-slate-200">
                  <td className="py-3 px-5 font-bold text-slate-900">Total account fees</td>
                  <td className="py-3 px-5 text-right font-bold text-rose-700">- {currency} {formatAmount(totalAccountFees)}</td>
                </tr>

                <tr className={`${isProfit ? 'bg-emerald-100' : 'bg-rose-100'} border-t-2 border-slate-300`}>
                  <td className="py-4 px-5 font-bold text-slate-900 uppercase text-xs tracking-widest">
                    {isProfit ? 'Net Profit' : 'Net Loss'}
                  </td>
                  <td className={`py-4 px-5 text-right font-bold text-lg ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {currency} {formatAmount(Math.abs(net))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }: { label: string; value: string; accent: 'emerald' | 'rose' }) => {
  const color = accent === 'emerald' ? 'text-emerald-600' : 'text-rose-600';
  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
};

const NetTile = ({ label, value, positive }: { label: string; value: string; positive: boolean }) => (
  <div className={`p-4 rounded-2xl border shadow-sm ${positive ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</p>
      {positive ? <TrendingUpIcon className="text-emerald-600" fontSize="small" /> : <TrendingDownIcon className="text-rose-600" fontSize="small" />}
    </div>
    <p className={`text-xl font-bold ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>{value}</p>
  </div>
);

const ComparisonBar = ({ label, value, max, color }: { label: string; value: number; max: number; color: string }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-bold text-slate-900">RWF {formatAmount(value)}</span>
      </div>
      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, currency = 'RWF' }: { label: string; value: number; currency?: string }) => (
  <tr className="border-b border-slate-100">
    <td className="py-3 px-5 text-slate-700">{label}</td>
    <td className="py-3 px-5 text-right text-slate-900 font-medium">{currency} {formatAmount(value)}</td>
  </tr>
);

export default RevenueReport;
